// Shared by the owner preview and server dispatch. No provider, Node, storage,
// or credential dependencies belong in this browser-safe module.
export const ZAPIER_PAYLOAD_MAX_BYTES = 128 * 1024;
export const ZAPIER_PAYLOAD_FIELDS = Object.freeze([
  'full_name', 'email', 'phone', 'campaign_id', 'campaign_name', 'form_id',
  'form_name', 'ad_name', 'meta_ad_id', 'meta_ad_set_id', 'status',
]);
export const ZAPIER_DEFAULT_FIELDS = Object.freeze([
  'full_name', 'email', 'phone', 'campaign_id', 'campaign_name', 'form_id', 'form_name',
]);
const FIELD_SOURCES = Object.freeze({
  full_name: 'contact_name', email: 'contact_email', phone: 'contact_phone',
  campaign_id: 'source_campaign_id', campaign_name: 'campaign_name',
  form_id: 'form_resource_id', form_name: 'form_label', ad_name: 'ad_label',
  meta_ad_id: 'provider_ad_id', meta_ad_set_id: 'provider_ad_set_id',
});
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const EXCLUDED_ANSWERS = new Set(['full_name', 'name', 'first_name', 'last_name',
  'email', 'phone_number', 'phone', '__proto__', 'prototype', 'constructor']);

export class ZapierPayloadError extends Error {
  constructor(code = 'zapier_payload_invalid') {
    super('The selected Zapier payload could not be prepared safely.');
    this.name = 'ZapierPayloadError';
    this.code = code === 'payload_too_large' ? code : 'zapier_payload_invalid';
  }
}
function invalid() { throw new ZapierPayloadError(); }
function identifier(value) {
  if (typeof value !== 'string' || !UUID.test(value)) invalid();
  return value.toLowerCase();
}
function timestamp(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d\d-\d\dT/.test(value) || !Number.isFinite(Date.parse(value))) invalid();
  return new Date(value).toISOString();
}
function selection(version) {
  if (!version || (version.schemaVersion ?? '1') !== '1') invalid();
  const { selectedFields, answerKeys } = version;
  if (!Array.isArray(selectedFields) || selectedFields.length > 11
    || new Set(selectedFields).size !== selectedFields.length
    || selectedFields.some(key => !ZAPIER_PAYLOAD_FIELDS.includes(key))) invalid();
  if (!Array.isArray(answerKeys) || answerKeys.length > 50 || new Set(answerKeys).size !== answerKeys.length
    || answerKeys.some(key => typeof key !== 'string' || !/^[a-z][a-z0-9_]{0,79}$/.test(key) || EXCLUDED_ANSWERS.has(key))) invalid();
  return { selectedFields, answerKeys };
}
function scalar(value) {
  if (value == null) return null;
  if (typeof value !== 'string' || value.length > 2000) invalid();
  return value;
}

export function serializeZapierPayload({ capture, version, eventId, isTest = false } = {}) {
  if (!capture || typeof capture !== 'object' || Array.isArray(capture) || typeof isTest !== 'boolean'
    || capture.pii_erased_at != null) invalid();
  const { selectedFields, answerKeys } = selection(version);
  const payload = {
    schema_version: '1', event_id: identifier(eventId), event_type: 'lead.created', is_test: isTest,
    lead_id: identifier(capture.id), captured_at: timestamp(capture.captured_at),
    submitted_at: timestamp(capture.provider_created_at), source: 'meta_instant_form',
  };
  // Stable schema order makes preview, synthetic tests and live delivery equal
  // regardless of checkbox or database array ordering.
  for (const key of ZAPIER_PAYLOAD_FIELDS) {
    if (selectedFields.includes(key)) payload[key] = key === 'status' ? 'new' : scalar(capture[FIELD_SOURCES[key]]);
  }
  if (answerKeys.length) {
    const answers = {};
    for (const key of [...answerKeys].sort()) {
      const stored = Object.hasOwn(capture.normalized_fields || {}, key) ? capture.normalized_fields[key] : [];
      if (!Array.isArray(stored) || stored.length > 20
        || stored.some(value => typeof value !== 'string' || value.length > 2000)) invalid();
      answers[key] = [...stored];
      payload[`answer_${key}`] = stored.length ? stored.join('; ') : null;
    }
    payload.answers = answers;
  }
  if (new TextEncoder().encode(JSON.stringify(payload)).byteLength > ZAPIER_PAYLOAD_MAX_BYTES) {
    throw new ZapierPayloadError('payload_too_large');
  }
  return payload;
}

export function createSyntheticZapierPayload({ version, testId, now = new Date().toISOString() } = {}) {
  const { answerKeys } = selection(version);
  const capturedAt = timestamp(now instanceof Date ? now.toISOString() : now);
  const fields = Object.fromEntries(answerKeys.map(key => [key,
    [key === 'project_type' ? 'Kitchen' : key === 'project_timing' ? 'Within 3 months' : 'Fictional sample']]));
  // These fixed fictional values never read campaign names, form labels, or a
  // real lead. The fresh test UUID is also its synthetic lead identity.
  return serializeZapierPayload({ version, eventId: testId, isTest: true, capture: {
    id: identifier(testId), captured_at: capturedAt,
    provider_created_at: new Date(Date.parse(capturedAt) - 4000).toISOString(),
    contact_name: 'Fictional Sample', contact_email: 'sample@example.com', contact_phone: '+1 613 555 0123',
    source_campaign_id: '00000000-0000-4000-8000-000000000606', campaign_name: 'Fictional kitchen campaign',
    form_resource_id: '00000000-0000-4000-8000-000000000607', form_label: 'Fictional enquiry form',
    ad_label: 'Fictional sample ad', provider_ad_id: '100000000000001', provider_ad_set_id: '100000000000002',
    normalized_fields: fields,
  } });
}
